import { useCallback, useEffect } from 'react'
import { kanbify } from '../../lib/kanbifyClient'
import { extractMentionTokens, normalizeMentionToken } from '../../lib/mentionParser'
import { trackProductEvent } from '../../lib/productAnalytics'
import { playSound } from '../../lib/sounds'

export default function useCardCommentsAndUploads({
    t,
    card,
    user,
    profile,
    members,
    companyMembers,
    commentText,
    setCommentText,
    submittingComment,
    setSubmittingComment,
    setCommentError,
    addComment,
    canContribute,
    uploadAttachment,
    linkAttachmentsToComment,
    fetchComments,
    fetchAttachments,
    setUploadingFile,
    setActiveTab,
    showAchievement,
    replyText,
    setReplyText,
    setReplyingTo,
    replySubmitting,
    setReplySubmitting,
    toggleCommentReaction,
}) {
    const notifyCommentTargets = useCallback((content) => {
        if (!content || !Array.isArray(companyMembers) || !card?.id) return

        const companyMemberById = new Map(companyMembers.map((member) => [member.id, member]))
        const tokens = extractMentionTokens(content, companyMembers)
        const tokenSet = new Set(tokens.map(normalizeMentionToken))
        const lowerContent = content.toLowerCase()
        const mentionedUsers = companyMembers.filter((member) => {
            if (member?.is_active === false) return false
            const label = (member.full_name || member.email || '').trim().toLowerCase()
            const slug = normalizeMentionToken(member.full_name || member.email || '')
            return member.id !== user?.id && (
                tokenSet.has(slug)
                || (label && lowerContent.includes(`@${label}`))
                || (slug && lowerContent.includes(`@${slug}`))
            )
        })

        const targets = new Map()
        const addTarget = (target, type = 'comment') => {
            if (!target?.id || target.id === user?.id || target.is_active === false) return
            const previous = targets.get(target.id)
            targets.set(target.id, {
                ...target,
                notificationType: previous?.notificationType === 'mention' || type === 'mention'
                    ? 'mention'
                    : 'comment',
            })
        }

        members.forEach((member) => addTarget(companyMemberById.get(member.id) || member))
        if (card?.created_by) addTarget(companyMemberById.get(card.created_by))
        mentionedUsers.forEach((target) => addTarget(target, 'mention'))

        if (mentionedUsers.length > 0) {
            trackProductEvent('mention_sent', {
                card_id: card.id,
                mention_count: mentionedUsers.length,
            })
        }

        Array.from(targets.values()).forEach((target) => {
            kanbify.functions.invoke('send-notification', {
                body: {
                    type: target.notificationType,
                    recipientId: target.id,
                    recipientEmail: target.email,
                    recipientName: target.full_name,
                    actorId: user?.id,
                    cardTitle: card.title,
                    cardId: card.id,
                    senderName: profile?.full_name || user?.email,
                    commentExcerpt: content.slice(0, 200),
                    message: content.slice(0, 200),
                    metadata: { source: 'card_comment' },
                },
            }).catch(() => { /* best-effort */ })
        })
    }, [card?.created_by, card?.id, card?.title, companyMembers, members, profile?.full_name, user?.email, user?.id])

    const handleAddComment = useCallback(async () => {
        if (!commentText.trim() || submittingComment || !user?.id) return
        setSubmittingComment(true)
        setCommentError('')
        try {
            const content = commentText.trim()
            const result = await addComment(user.id, content)
            if (!result) {
                setCommentError(t('card.comments.errorSend'))
            } else {
                setCommentText('')
                notifyCommentTargets(content)
            }
        } catch (error) {
            setCommentError(t('card.comments.errorGeneric', { message: error.message }))
        }
        setSubmittingComment(false)
    }, [addComment, commentText, notifyCommentTargets, setCommentError, setCommentText, setSubmittingComment, submittingComment, t, user?.id])

    const handleCommentImageUpload = useCallback(async (files, parentId = null, captionOverride = null, onSuccess = null) => {
        const imageFiles = Array.from(files || []).filter((file) => file?.type?.startsWith('image/'))
        if (imageFiles.length === 0 || !user?.id) return
        if (!canContribute) {
            showAchievement({ type: 'error', message: t('card.attachments.claimFirst') })
            return
        }
        const tooLarge = imageFiles.find((file) => file.size > 10 * 1024 * 1024)
        if (tooLarge) {
            showAchievement({ type: 'error', message: t('card.attachments.tooLarge') })
            return
        }
        setUploadingFile(true)
        try {
            const uploaded = []
            const errors = []
            for (const file of imageFiles) {
                const { data, error } = await uploadAttachment(file, user.id)
                if (error) errors.push(error)
                else if (data) uploaded.push(data)
            }
            if (uploaded.length > 0) {
                const caption = (captionOverride !== null ? captionOverride : commentText).trim()
                const fallbackText = uploaded.length === 1
                    ? `Imagine încărcată: ${uploaded[0].name}`
                    : `${uploaded.length} imagini încărcate`
                const comment = await addComment(user.id, caption || fallbackText, parentId)
                if (!comment) {
                    setCommentError(t('card.comments.errorSend'))
                } else {
                    const linked = await linkAttachmentsToComment(uploaded.map((attachment) => attachment.id), comment.id)
                    if (!linked.success) {
                        setCommentError(linked.error || t('card.comments.errorSend'))
                    }
                    if (caption) notifyCommentTargets(caption)
                    if (onSuccess) onSuccess()
                    else if (caption) setCommentText('')
                    await Promise.all([fetchComments(), fetchAttachments()])
                    playSound('check')
                    setActiveTab('comments')
                    showAchievement({ type: 'success', message: imageFiles.length === 1 ? t('card.attachments.uploaded', { name: imageFiles[0].name }) : t('card.attachments.uploadedMulti', { count: uploaded.length }) })
                }
            }
            if (errors.length > 0 && uploaded.length === 0) {
                showAchievement({ type: 'error', message: t('card.attachments.uploadError', { error: errors[0] }) })
            } else if (errors.length > 0) {
                showAchievement({ type: 'error', message: t('card.attachments.partialError', { ok: uploaded.length, fail: errors.length }) })
            }
        } finally {
            setUploadingFile(false)
        }
    }, [user?.id, canContribute, uploadAttachment, addComment, commentText, linkAttachmentsToComment, fetchComments, fetchAttachments, notifyCommentTargets, showAchievement, t, setActiveTab, setCommentError, setCommentText, setUploadingFile])

    const handleAddReply = useCallback(async (parentId) => {
        const content = replyText.trim()
        if (!content || replySubmitting || !user?.id) return
        setReplySubmitting(true)
        try {
            const result = await addComment(user.id, content, parentId)
            if (result) {
                setReplyText('')
                setReplyingTo(null)
                notifyCommentTargets(content)
            } else {
                showAchievement({ type: 'error', message: t('card.comments.errorSend') })
            }
        } catch (error) {
            showAchievement({ type: 'error', message: t('card.comments.errorGeneric', { message: error.message }) })
        } finally {
            setReplySubmitting(false)
        }
    }, [replyText, replySubmitting, user?.id, addComment, notifyCommentTargets, showAchievement, t, setReplySubmitting, setReplyText, setReplyingTo])

    const handleToggleCommentReaction = useCallback((commentId, emoji) => {
        if (!user?.id) return
        toggleCommentReaction(commentId, user.id, emoji)
    }, [user?.id, toggleCommentReaction])

    const handlePasteUpload = useCallback(async (file) => {
        if (!file || !user?.id) return
        if (file.type?.startsWith('image/')) {
            await handleCommentImageUpload([file])
            return
        }
        if (!canContribute) {
            showAchievement({ type: 'error', message: t('card.attachments.claimFirst') })
            return
        }
        if (file.size > 10 * 1024 * 1024) {
            showAchievement({ type: 'error', message: t('card.attachments.tooLarge') })
            return
        }
        setUploadingFile(true)
        const { error } = await uploadAttachment(file, user.id)
        if (error) {
            showAchievement({ type: 'error', message: t('card.attachments.uploadError', { error }) })
        } else {
            playSound('check')
            showAchievement({ type: 'success', message: t('card.attachments.uploaded', { name: file.name }) })
        }
        setUploadingFile(false)
    }, [user?.id, canContribute, handleCommentImageUpload, uploadAttachment, showAchievement, t, setUploadingFile])

    useEffect(() => {
        const handler = (event) => {
            if (!card) return
            const items = event.clipboardData?.items
            if (!items) return
            for (const item of items) {
                if (item.kind === 'file') {
                    event.preventDefault()
                    const file = item.getAsFile()
                    if (file) handlePasteUpload(file)
                    return
                }
            }
        }
        document.addEventListener('paste', handler)
        return () => document.removeEventListener('paste', handler)
    }, [card, handlePasteUpload])

    return {
        handleAddComment,
        handleCommentImageUpload,
        handleAddReply,
        handleToggleCommentReaction,
    }
}
